import type { ReactNode } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export const EditorRequiredIndicator = () => (
  <Box aria-hidden='true' color='#f00' component='span'>
    *
  </Box>
);

interface EditorSectionProps {
  cardTestId?: string;
  children: ReactNode;
  description?: string;
  headerAction?: ReactNode;
  required?: boolean;
  title: string;
  titleId: string;
}

export const EditorSection = ({
  cardTestId,
  children,
  description,
  headerAction,
  required = false,
  title,
  titleId,
}: EditorSectionProps) => (
  <Paper
    aria-labelledby={titleId}
    component='section'
    data-testid={cardTestId}
    elevation={0}
    sx={{
      backgroundColor: 'background.paper',
      border: '1px solid #dce9f7',
      borderRadius: 2,
      boxShadow: '0 4px 16px rgba(42, 56, 66, 0.08)',
      boxSizing: 'border-box',
      minWidth: 0,
      overflow: 'hidden',
      width: '100%',
    }}
  >
    <Box
      component='header'
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
        paddingX: { xs: 2, sm: 3 },
        paddingY: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack
        alignItems={{ xs: 'stretch', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent='space-between'
        spacing={{ xs: 1.5, sm: 2 }}
      >
        <Stack flex='1 1 auto' minWidth={0} spacing={0.5}>
          <Typography
            color='text.primary'
            component='h2'
            fontWeight={600}
            id={titleId}
            variant='h5'
          >
            {title}
            {required && <EditorRequiredIndicator />}
          </Typography>
          {description && (
            <Typography
              color='text.secondary'
              component='p'
              fontWeight={400}
              lineHeight='22px'
              variant='body2'
            >
              {description}
            </Typography>
          )}
        </Stack>
        {headerAction && (
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 'auto' } }}>{headerAction}</Box>
        )}
      </Stack>
    </Box>

    <Box
      sx={{
        backgroundColor: 'background.paper',
        boxSizing: 'border-box',
        minWidth: 0,
        padding: { xs: 2, sm: 3 },
        width: '100%',
      }}
    >
      {children}
    </Box>
  </Paper>
);
